import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { provideRouter, withComponentInputBinding, withEnabledBlockingInitialNavigation, withInMemoryScrolling, withPreloading, withRouterConfig, TitleStrategy, Router } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withInterceptors, withFetch } from '@angular/common/http';
import { provideAngularSvgIcon } from 'angular-svg-icon';

import { ReactiveFormsModule } from '@angular/forms';

import { routes } from './app.routes';
import { environment } from '@environments/environment';

// Generated OpenAPI Services Configuration
import { provideApi } from '@api/provide-api';
import { provideCopilotChat } from './features/copilot-chat/copilot-chat.providers';
import { apiResponseInterceptor } from '@core/interceptors/api-response.interceptor';
import { authInterceptor } from '@core/interceptors/auth.interceptor';
import { errorInterceptor } from '@core/interceptors/error.interceptor';
import { loadingInterceptor } from '@core/interceptors/loading.interceptor';
import { MockApiInterceptor } from '@core/interceptors/mock-api.interceptor';
import { cacheInterceptor } from '@core/interceptors/cache.interceptor';
import { tenantInterceptor } from '@core/interceptors/tenant.interceptor';
import { requestIdInterceptor } from '@core/interceptors/request-id.interceptor';
import { requestTimelineInterceptor } from '@core/interceptors/request-timeline.interceptor';
import { retryInterceptor } from '@core/interceptors/retry.interceptor';
import { PerformanceTrackerService } from '@core/services/performance-tracker.service';
import { AuthService } from '@core/services/auth.service';
import { TenantStore } from './stores/tenant.store';


// Legacy Services - keeping for gradual migration
import { ToastService as MessageService } from '@shared/components/ui/toast/toast.service';
import { ConfirmationService } from '@shared/components/ui/confirm-dialog/confirm-dialog.service';

import { WebSocketService } from '@core/services/websocket.service';
import { MockWebSocketService } from '@core/services/mock-websocket.service';
import { LoggerService } from '@core/services/logger.service';
import { DynamicDialogService } from '@core/services/dialog.service';
import { createSentryLogHandler } from '@core/services/sentry.service';
import * as Sentry from '@sentry/angular';
import { isAnyHttpMockActive, isAnyWsMockActive } from '@core/config/mock-flags';
import { CustomPreloadingStrategy } from '@core/strategies/custom-preloading.strategy';
import { PageTitleStrategy } from '@core/strategies/page-title.strategy';
import { initializeEnvironment, validateEnvironment, initializeFeatureFlags } from '@core/initializers/environment.initializer';
import { withBootstrapTimeout } from '@core/initializers/bootstrap-timeout';
import { appInitializerProviders } from '@core/initializers/app.initializer';
import { SvgIconPreloaderService } from '@core/services/svg-icon-preloader.service';

export const appConfig: ApplicationConfig = {
  providers: [
    // Router configuration
    provideRouter(
      routes,
      withComponentInputBinding(),
      withEnabledBlockingInitialNavigation(),
      withInMemoryScrolling({
        // #17 · 'enabled' restores the prior scroll offset on browser Back/Forward
        // (popstate) and resets to top for new forward navigations. anchorScrolling
        // is an independent option, so #fragment links keep working.
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled'
      }),
      withPreloading(CustomPreloadingStrategy),
      // Angular v22 changed the default paramsInheritanceStrategy from
      // 'emptyOnly' to 'always'; pin 'emptyOnly' to preserve the pre-upgrade
      // route-parameter inheritance behaviour (no behavioural regression).
      withRouterConfig({ paramsInheritanceStrategy: 'emptyOnly' })
    ),

    // HTTP client with interceptors
    // CRITICAL: Interceptor order matters for response handling!
    // - Requests flow in array order: 0 -> 1 -> 2 -> ... -> N -> server
    // - Responses flow in REVERSE order: server -> N -> ... -> 2 -> 1 -> 0
    //
    // For 401 handling: authInterceptor must process responses BEFORE errorInterceptor
    // so it can attempt token refresh before any error notification is shown.
    // By placing errorInterceptor BEFORE authInterceptor in the array,
    // authInterceptor processes responses first (due to reverse order).
    provideHttpClient(
      withFetch(),
      withInterceptors([
        apiResponseInterceptor, // Unwrap ApiResponse<T> envelope — extracts .data from successful responses
        requestIdInterceptor, // Add request ID to all requests
        requestTimelineInterceptor, // GAP-044: feed DebugLogger "Request Timeline" with real API traffic (dev-only; gated on enableDevTools — same flag that ships the timeline route)
        tenantInterceptor, // Add tenant ID for multi-tenancy
        // SECURITY(SE-02): CSRF protection is handled by authInterceptor (below), which reads the
        // token from in-memory store and sends X-CSRF-TOKEN header on every authenticated request.
        // Backend validates header against the HttpOnly csrf_token cookie (double-submit pattern).
        // SECURITY (GAP-017, GAP-C18): MockApiInterceptor registration.
        // - NEVER active in production: isAnyHttpMockActive() has a !environment.production guard.
        // - Flag list is centralized in core/config/mock-flags.ts (single source of truth).
        // - GAP-C18: Removed localStorage override — controlled only via environment config.
        ...(isAnyHttpMockActive() ? [MockApiInterceptor] : []),
        cacheInterceptor, // Cache responses for better performance
        // SENTRY-NOISE-CLUSTER: errorInterceptor must WRAP retryInterceptor so a
        // transient failure is reported to Sentry exactly ONCE (after retries are
        // exhausted), not once per attempt. Responses flow in reverse array order,
        // so the interceptor listed FIRST here is the OUTERMOST on the response
        // path. Previously retryInterceptor was listed first (outer), which made
        // errorInterceptor its inner handler — the retry operator re-subscribed the
        // errorInterceptor pipeline on every attempt, so a single 5xx/network GET
        // was reported to Sentry up to 4× (original + 3 retries). With errorInterceptor
        // outer, retries still happen silently (a recovered request never reaches it)
        // and only the final, exhausted error surfaces — once.
        errorInterceptor, // Error handling - placed BEFORE authInterceptor so auth processes 401s first on response
        // GAP-FE-033 · retry idempotent (GET/HEAD) on 5xx + network failures with
        // exponential backoff + jitter. Placed AFTER errorInterceptor (inner) so
        // retries are exhausted before errorInterceptor reports/toasts, and BEFORE
        // authInterceptor (outer of auth) so the retried request re-attaches a fresh token.
        retryInterceptor,
        authInterceptor, // Auth token + 401 refresh - processes responses BEFORE errorInterceptor due to reverse order
        loadingInterceptor
      ])
    ),

    // Animations — RF-QA-173 · the animation ENGINE
    // (`@angular/animations/browser`, 64.3 KiB minified) is loaded through a
    // dynamic import on the first animated render instead of sitting in the
    // shell's initial graph. No component in the app shell declares
    // `animations: [...]`; every one of the 13 that do (schema-management,
    // data-quality, symbol-mapping layout, ml-anomaly-detection, users import
    // dialog) lives behind a lazy route, so the engine is fetched alongside the
    // route chunk that needs it and never on the critical path.
    provideAnimationsAsync(),

    // Interactive copilot (M1) — registers the "Investigate with Copilot"
    // command-palette entry. No-op when environment.features.enableCopilotChat is false.
    provideCopilotChat(),

    // SVG Icon Support (for Heroicons, Tabler Icons)
    provideAngularSvgIcon(),

    // Generated OpenAPI Services - configure basePath and credentials
    // NOTE: OpenAPI-generated services have '/api/v1' hardcoded in paths,
    // so basePath must be the domain only (not including '/api/v1')
    provideApi({
      basePath: environment.apiUrl.replace(/\/api\/v1\/?$/, ''),
      withCredentials: true, // Send cookies for auth
    }),

    // Signal stores are automatically provided via @ngrx/signals
    // Each store uses signalStore with { providedIn: 'root' }

    // Legacy services - migrated to Razor UI
    // ToastService and DynamicDynamicDialogService from Razor UI are provided in root, no provider needed here
    MessageService, // Legacy: Toast notifications (migrating to ToastService)
    ConfirmationService, // Legacy: Confirmation dialogs (migrating to signal-based modals)

    // WebSocket Service - Use mock when WS-affecting mock flags are enabled.
    // SECURITY (GAP-016, GAP-C18): MockWebSocketService is NEVER instantiated in production.
    // isAnyWsMockActive() has a !environment.production guard and checks only WS-relevant flags
    // (Tenant/SymbolMapping/Schema are HTTP-only — see MOCK_FLAG_DEFINITIONS).
    {
      provide: WebSocketService,
      useClass: isAnyWsMockActive() ? MockWebSocketService : WebSocketService
    },

    // Import reactive forms module (no longer importing Material modules)
    importProvidersFrom(
      ReactiveFormsModule
    ),

    // Security Services
    LoggerService,
    DynamicDialogService, // ✅ Migrated to legacy library DynamicDialogService
    // SanitizationService is `providedIn: 'root'`; RF-QA-173 dropped the
    // redundant provider entry because it was the ONLY static edge from
    // app.config.ts into sanitization.service.ts, which dragged DOMPurify
    // (28.5 KiB) + the service (8.4 KiB) into the shell's initial graph. DI is
    // unchanged — every injector still resolves the same root-scoped service —
    // but DOMPurify now lands in the lazy chunks that actually sanitize.
    // SentryService is provided via providedIn: 'root', no need to list here

    // Custom Preloading Strategy
    CustomPreloadingStrategy,

    // Page Title Strategy - Updates browser title on navigation (HIGH-002)
    { provide: TitleStrategy, useClass: PageTitleStrategy },

    // Application Initializers - Run before app starts.
    // Migrated from APP_INITIALIZER (deprecated since Angular 19) to
    // provideAppInitializer; semantics preserved.
    provideAppInitializer(initializeEnvironment()),
    provideAppInitializer(validateEnvironment()),
    provideAppInitializer(initializeFeatureFlags()),
    // BOOTSTRAP-HANG FIX (incident 2026-07-01): initializeSession() awaits an
    // unbounded /auth/refresh (+ effective-permissions); on an intermittent
    // concurrent-refresh / backend stall the awaited promise never settles and
    // the "INITIALIZING" splash hangs forever. withBootstrapTimeout() bounds the
    // wait and lets bootstrap proceed (guards + interceptor refresh-retry then
    // drive login/redirect) instead of wedging startup. It never rejects, so it
    // can't trip main.ts's bootstrap error box. The refresh itself still owns
    // logout on a genuine failure inside initializeSession().
    provideAppInitializer(() => {
      const auth = inject(AuthService);
      const logger = inject(LoggerService);
      return withBootstrapTimeout(auth.initializeSession(), 'initializeSession', logger);
    }),
    // F1b COLD-LOAD RACE FIX: establish the user's current tenant BEFORE the
    // router activates. initializeSession() (above) restores auth + effective
    // permissions; this restores the tenant context into TenantStore so that on
    // a cold load (full reload / deep-link into a gated route) tenantGuard finds
    // currentTenant already set and takes its fast-path instead of triggering the
    // async auto-switch that races permissionGuard → /unauthorized. Awaited (the
    // initializer returns the Promise) so it completes before blocking initial
    // navigation. Fail-safe: establishTenantContext() never throws and leaves
    // currentTenant unset on failure, letting the guard chain decide. Also bounded
    // (same incident) — its loadAvailableTenants/getCurrentTenant calls are
    // likewise unbounded and must not wedge bootstrap.
    provideAppInitializer(() => {
      const tenant = inject(TenantStore);
      const logger = inject(LoggerService);
      return withBootstrapTimeout(tenant.establishTenantContext(), 'establishTenantContext', logger);
    }),
    // Custom theme + accessibility initializers (GAP-FE-017)
    // - Custom colors, font, border-radius from localStorage (validated)
    // - A11y keyboard shortcuts (Alt+N/C/S/T) + focus management
    // Dark/light mode FOUC is handled by inline <script> in index.html
    ...appInitializerProviders,
    // SVG Icon Preloader - pre-caches sidebar icons to prevent duplicate HTTP requests.
    // RF-QA-174: armed on the authenticated, permission-hydrated session (a cold
    // login is warmed too) instead of the bootstrap-time `razor_session_active`
    // localStorage flag, which skipped every first login. Fire-and-forget: does
    // NOT block app startup (icon preloading is an optimization, not required).
    provideAppInitializer(() => {
      inject(SvgIconPreloaderService).preloadWhenAuthenticated();
    }),
    // Configure LoggerService with Sentry integration
    provideAppInitializer(() => {
      inject(LoggerService).configure({
        externalHandler: createSentryLogHandler(),
      });
    }),
    // Sentry TraceService for router integration (performance monitoring)
    // This enables automatic transaction creation for Angular route changes
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    // Empty initializer exists solely to ensure TraceService is instantiated at
    // bootstrap. TraceService must be injected early to capture all router
    // navigations. This is the official Sentry Angular integration pattern.
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    // Performance Tracking (GAP-014): instantiating PerformanceTrackerService
    // at bootstrap is sufficient — its constructor sets up router subscriptions
    // and registers PerformanceObservers for LCP, CLS, and resource timing.
    // All work is gated on the PERFORMANCE_TRACKING_ENABLED token.
    provideAppInitializer(() => {
      inject(PerformanceTrackerService);
    }),

    // Custom providers can be added here
  ]
};
